// ════════════════════════════════════════════════════════════════════
// CLIENT FORMS & DOCUMENTS PANEL
//
// Mounts inside HealthClientProfileModal as a SectionBlock. One-stop
// view of EVERY compliance form tied to this client: SOA, ACA consent,
// future PTC / eligibility-attestation forms — anything that becomes
// signed proof in an audit.
//
// Per-row data (see /api/audit/packet → forms[]):
//   • Form type — Medicare SOA · ACA Consent · …
//   • Status — pending_client · pending_agent · completed · expired · revoked
//   • Signed timestamps — client signed (with IP/UA) + agent signed
//   • Compliance flag — green (compliant) · amber (same_day_exception) ·
//                       red (non_compliant)
//   • View certificate — opens the HMAC-signed cert URL (no JWT needed
//                        for the cert opener; tab-shareable)
//   • Quick actions: Resend SMS+Email · Revoke
//
// "Missing" rows: if there's no SOA at all for an SOA-eligible client,
// shows a "Capture now" CTA that opens the existing send flow.
//
// Reads /api/audit/packet?client_id=… as the single source of truth so
// the panel and the audit pack stay consistent.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { FileCheck, FileText, Clock, ShieldCheck, ShieldAlert, ShieldX, ExternalLink, RefreshCw, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

const FORM_TYPE_LABELS = {
  medicare_soa: 'Medicare SOA',
  aca_consent: 'ACA Consent',
};

const STATUS_LABELS = {
  pending_client: 'Pending client signature',
  pending_agent:  'Pending agent countersign',
  completed:      'Completed',
  expired:        'Expired',
  revoked:        'Revoked',
};

const STATUS_COLORS = {
  pending_client: 'bg-amber-50 border-amber-300 text-amber-900',
  pending_agent:  'bg-sky-50 border-sky-300 text-sky-900',
  completed:      'bg-emerald-50 border-emerald-300 text-emerald-900',
  expired:        'bg-slate-100 border-slate-300 text-slate-600',
  revoked:        'bg-rose-50 border-rose-300 text-rose-900',
};

// Stamp every visible timestamp with the operator's local timezone
// abbreviation so it's unambiguous which zone they're seeing. Database
// stores everything UTC; this is purely a display convenience so an
// auditor (or a PST agent) doesn't have to guess what zone "3:14 AM"
// is. The Intl call falls back gracefully if the runtime is old.
function localTzAbbr() {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    const tz = parts.find(p => p.type === 'timeZoneName');
    return tz ? tz.value : '';
  } catch { return ''; }
}
const TZ_ABBR = localTzAbbr();

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    const local = new Date(ts).toLocaleString();
    return TZ_ABBR ? `${local} ${TZ_ABBR}` : local;
  } catch { return String(ts); }
}

function ComplianceBadge({ flag }) {
  if (flag === 'compliant') return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-emerald-100 text-emerald-900 border border-emerald-300">
      <ShieldCheck size={11} /> Compliant
    </span>
  );
  if (flag === 'same_day_exception') return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300">
      <ShieldAlert size={11} /> Same-day exception
    </span>
  );
  if (flag === 'non_compliant') return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-rose-100 text-rose-900 border border-rose-300">
      <ShieldX size={11} /> Non-compliant
    </span>
  );
  return null;
}

export default function ClientFormsPanel({ clientId, clientName, onCaptureNow, showToast = () => {} }) {
  const [packet, setPacket]   = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);
  const [busyId, setBusyId]   = React.useState(null);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const out = await apiFetch(`/api/audit/packet?client_id=${encodeURIComponent(clientId)}`);
      setPacket(out);
    } catch (e) {
      setError(e.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  React.useEffect(() => { load(); }, [load]);

  const forms = packet?.forms || [];
  const verify = packet?.verify || null;
  const gaps = packet?.gaps || {};

  // Open the certificate as a shareable HMAC-signed URL. cert-link
  // mints a short-lived URL with sig=… so the new tab works even
  // without an operator JWT.
  async function openCertificate(form) {
    if (!form?.id) return;
    setBusyId(form.id);
    try {
      const out = await apiFetch(`/api/soa-v2/cert-link?soa_id=${encodeURIComponent(form.id)}`);
      if (out?.url) window.open(out.url, '_blank', 'noopener');
      else throw new Error('No cert URL returned');
    } catch (e) {
      showToast?.(`Certificate link failed: ${e.message}`, 'rose');
    } finally {
      setBusyId(null);
    }
  }

  // Re-send the signing link to the client (mints a fresh token + new
  // SMS/email; the original link stays single-use). Backend records
  // an audit event for the re-send.
  async function resend(form) {
    if (!form?.id) return;
    setBusyId(form.id);
    try {
      await apiFetch('/api/soa-v2/resend', {
        method: 'POST',
        body:   JSON.stringify({ soa_id: form.id }),
      });
      showToast?.('Resent. Fresh signing link delivered.', 'sky');
      await load();
    } catch (e) {
      showToast?.(`Resend failed: ${e.message}`, 'rose');
    } finally {
      setBusyId(null);
    }
  }

  // Open the existing "Send SOA" flow for this client. The parent
  // modal stashes prefill on window + navigates to the SOA tab so the
  // SendSoaModal opens already populated.
  function captureNow(formType) {
    if (typeof onCaptureNow === 'function') onCaptureNow(formType);
  }

  if (loading) return <div className="text-sm text-slate-500 italic">Loading forms…</div>;
  if (error)   return <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2">{error}</div>;

  return (
    <div className="space-y-4">
      {/* Chain-verification stamp — the single line that proves the trail
          for this client is intact. Green if verifier says ok. */}
      {verify && (
        <div className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide px-3 py-1.5 border ${
          verify.ok ? 'bg-emerald-50 text-emerald-900 border-emerald-300' : 'bg-rose-50 text-rose-900 border-rose-300'
        }`}>
          <ShieldCheck size={13} />
          {verify.ok
            ? `Chain verified — ${verify.events_checked} event(s)`
            : `Chain BROKEN at ${verify.broken_at_id?.slice(0,8) || '?'} — ${verify.broken_reason}`}
        </div>
      )}

      {/* Missing rows — show a "Capture now" affordance per form type
          this client is missing entirely. */}
      <MissingRow
        clientId={clientId}
        forms={forms}
        gaps={gaps}
        onCaptureNow={captureNow}
      />

      {/* Existing form rows. */}
      {forms.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-3 py-3 border border-dashed border-slate-300">
          No compliance forms on file for this client yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {forms.map(f => (
            <li key={f.id} className={`border-l-4 px-3 py-2.5 ${STATUS_COLORS[f.status] || 'border-slate-300 bg-white'}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-black uppercase tracking-[0.15em]">{FORM_TYPE_LABELS[f.form_type] || f.form_type}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-white/70 border border-slate-300">{STATUS_LABELS[f.status] || f.status}</span>
                    <ComplianceBadge flag={f.compliance_flag} />
                  </div>
                  <div className="text-[11px] mt-1 text-slate-700 space-y-0.5">
                    <div className="flex items-center gap-2"><Clock size={11}/> <span>Sent {fmtTs(f.created_at)}</span></div>
                    {f.signatures?.client && (
                      <div className="flex items-center gap-2"><FileCheck size={11} className="text-emerald-700"/>
                        <span>Client signed {fmtTs(f.signatures.client.signed_at)} · IP {f.signatures.client.ip_address || '—'}</span>
                      </div>
                    )}
                    {f.signatures?.agent && (
                      <div className="flex items-center gap-2"><FileCheck size={11} className="text-emerald-700"/>
                        <span>Agent signed {fmtTs(f.signatures.agent.signed_at)} · IP {f.signatures.agent.ip_address || '—'}</span>
                      </div>
                    )}
                    {f.exception_reason && (
                      <div className="text-[10px] text-amber-900 italic">Exception: {f.exception_reason}</div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 items-stretch min-w-[140px]">
                  {f.status === 'completed' && (
                    <button
                      onClick={() => openCertificate(f)}
                      disabled={busyId === f.id}
                      className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                    >
                      <ExternalLink size={11}/> View cert
                    </button>
                  )}
                  {(f.status === 'pending_client' || f.status === 'pending_agent') && (
                    <button
                      onClick={() => resend(f)}
                      disabled={busyId === f.id}
                      className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                    >
                      <RefreshCw size={11}/> Resend
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Footer — full audit-packet download link. The packet endpoint
          returns JSON; UI download is just a JSON dump for now. The PDF
          view is a future iteration. */}
      <div className="text-[10px] text-slate-500 pt-1 flex items-center gap-3 flex-wrap">
        <span>Source: server-captured timestamps + IPs · append-only hash chain</span>
        <span>·</span>
        <a
          href={`/api/audit/packet?client_id=${encodeURIComponent(clientId)}`}
          target="_blank" rel="noopener noreferrer"
          className="underline hover:no-underline text-sky-700"
        >
          Open full audit packet (JSON)
        </a>
      </div>
    </div>
  );
}

// Sub-component: shows a single "Missing" CTA for each form-type the
// client doesn't have a record of yet. Keeps the panel honest — if
// there's no SOA, the panel shows it visibly, not silently.
function MissingRow({ forms, gaps, onCaptureNow }) {
  const haveSoa     = forms.some(f => f.form_type === 'medicare_soa');
  const haveConsent = forms.some(f => f.form_type === 'aca_consent');

  // Use gaps verdict (canSubmitEnrollment) to decide whether to surface
  // missing-form CTAs per vertical. If the vertical isn't applicable
  // (e.g. client is ACA-only), the Medicare gap is just informational.
  const showSoaMissing     = !haveSoa     && gaps?.medicare?.missing?.some(m => m.key === 'soa_signed');
  const showConsentMissing = !haveConsent && gaps?.aca?.missing?.some(m => m.key === 'aca_consent_signed');

  if (!showSoaMissing && !showConsentMissing) return null;

  return (
    <div className="space-y-2">
      {showSoaMissing && (
        <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px]">
            <span className="font-black uppercase tracking-[0.15em] text-amber-900">Missing — Medicare SOA</span>
            <span className="ml-2 text-amber-800">No Scope of Appointment on file. Required before any Medicare plan discussion.</span>
          </div>
          <button
            onClick={() => onCaptureNow('medicare_soa')}
            className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 bg-amber-900 text-white hover:bg-amber-800 inline-flex items-center gap-1.5"
          >
            <Send size={11}/> Capture now
          </button>
        </div>
      )}
      {showConsentMissing && (
        <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px]">
            <span className="font-black uppercase tracking-[0.15em] text-amber-900">Missing — ACA Consent</span>
            <span className="ml-2 text-amber-800">No Marketplace consent on file. Required before any HealthCare.gov assistance.</span>
          </div>
          <button
            onClick={() => onCaptureNow('aca_consent')}
            className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 bg-amber-900 text-white hover:bg-amber-800 inline-flex items-center gap-1.5"
          >
            <Send size={11}/> Capture now
          </button>
        </div>
      )}
    </div>
  );
}
